// ════════════════════════════════════════════════════════════════════
// Glass-i — Stage 6Q.1 Proforma API client + master loaders.
// Mirrors the otherChargesApi pattern (axios client + unwrap()).
// ════════════════════════════════════════════════════════════════════

import client from '../../../../api/client';

const BASE = '/api/erp/sales/proformas';

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined ? resp.data.data : (resp ? resp.data : null);
}

export const proformaApi = {
  // Stage SALES-PROFORMA-LIST-UX-1 — server-side list controls. Params
  // (status/search/limit/offset/dateFrom/dateTo/salespersonEmployeeId) pass
  // straight through; the backend validates + clamps limit to 100.
  list: (params = {}) => client.get(BASE, { params }).then(unwrap),
  // Per-status tab counts honouring the same context filters as list().
  statusCounts: (params = {}) => client.get(`${BASE}/status-counts`, { params }).then(unwrap),
  get: (id) => client.get(`${BASE}/${encodeURIComponent(id)}`).then(unwrap),
  create: (payload = {}) => client.post(BASE, payload).then(unwrap),
  update: (id, payload = {}) => client.put(`${BASE}/${encodeURIComponent(id)}`, payload).then(unwrap),
  // Stage SALES-PROFORMA-CLONE-1 — duplicate an existing proforma into a new
  // PENDING draft (all content copied server-side; lifecycle reset). Returns the
  // new proforma so the caller can navigate straight into its editor.
  clone: (id) => client.post(`${BASE}/${encodeURIComponent(id)}/clone`).then(unwrap),
  // Stage SALES-PROFORMA-UI-FLOW-1 — soft delete (backend sets deleted_at).
  remove: (id) => client.delete(`${BASE}/${encodeURIComponent(id)}`).then(unwrap),
  // Stage SALES-PROFORMA-STATUS-1 — lifecycle transitions. NOTE (Stage
  // SALES-PROFORMA-APPROVAL-1): confirm/hold/cancel now RAISE an L1 approval
  // request from a PENDING proforma (they no longer transition directly).
  confirm: (id) => client.post(`${BASE}/${encodeURIComponent(id)}/confirm`).then(unwrap),
  hold: (id, reason) => client.post(`${BASE}/${encodeURIComponent(id)}/hold`, { reason }).then(unwrap),
  reopen: (id) => client.post(`${BASE}/${encodeURIComponent(id)}/reopen-pending`).then(unwrap),
  cancel: (id, reason) => client.post(`${BASE}/${encodeURIComponent(id)}/cancel`, { reason }).then(unwrap),
  // Stage SALES-PROFORMA-APPROVAL-1 — raise an L1 status-change request +
  // read per-proforma approvals and Work-Order readiness (no WO created).
  requestStatusChange: (id, body = {}) =>
    client.post(`${BASE}/${encodeURIComponent(id)}/request-status-change`, body).then(unwrap),
  approvals: (id) => client.get(`${BASE}/${encodeURIComponent(id)}/approvals`).then(unwrap),
  workOrderReadiness: (id) => client.get(`${BASE}/${encodeURIComponent(id)}/work-order-readiness`).then(unwrap),
  preview: (payload = {}) => client.post(`${BASE}/preview`, payload).then(unwrap),
  // Stage 6PL-1c — `params` may carry { customerId, date }. With them the
  // fabrication + process rates in the payload are THIS customer's negotiated
  // rates; without them it is the master payload it has always been.
  getDropdowns: (params = {}) => client.get(`${BASE}/dropdowns`, { params }).then(unwrap),
  // Customer-aware item picker (see the route's comment): the same item search,
  // with the customer's price list already applied to salesRate.
  itemLookup: (params = {}) => client.get(`${BASE}/item-lookup`, { params }).then(unwrap),
  // Stage SALES-PROFORMA-ACTIVITY-1 — unified activity feed (system events +
  // manual comments) and comment append. Read-only feed; append-only post.
  activity: (id) => client.get(`${BASE}/${encodeURIComponent(id)}/activity`).then(unwrap),
  addComment: (id, body) => client.post(`${BASE}/${encodeURIComponent(id)}/comments`, { body }).then(unwrap),
  // Expand a combo / set item into { combo, components } (read-only).
  expandCombo: (itemId) => client.get(`${BASE}/combo/${encodeURIComponent(itemId)}/expand`).then(unwrap),
  // Stage SALES-PROFORMA-1 — resolve a customer's HRMS salesperson (+ optional
  // manual-override preview). Read-only; never mutates the customer mapping.
  customerSalesperson: (customerId, params = {}) =>
    client.get(`${BASE}/customers/${encodeURIComponent(customerId)}/salesperson`, { params }).then(unwrap),
  // Stage WO-3.3 — post-cut Change Order. cutPieces feeds the workbench modal;
  // changeOrder posts the per-piece fates + the L1 MFA challenge id (in payload);
  // releaseNewSize appends the freshly-added new-size line's pieces to the WO.
  changeOrderCutPieces: (id) =>
    client.get(`${BASE}/${encodeURIComponent(id)}/change-order/cut-pieces`).then(unwrap),
  changeOrder: (id, payload = {}) =>
    client.post(`${BASE}/${encodeURIComponent(id)}/change-order`, payload).then(unwrap),
  releaseNewSize: (id) =>
    client.post(`${BASE}/${encodeURIComponent(id)}/release-new-size`).then(unwrap),
  // WO-3.1 — pre-cut Recall: withdraw the un-cut WO + reopen to edit (L1 MFA
  // challenge id travels in the payload, like changeOrder).
  recall: (id, payload = {}) =>
    client.post(`${BASE}/${encodeURIComponent(id)}/recall`, payload).then(unwrap)
};

// Customer + item lookups come from their own master endpoints (never
// hardcoded). Both degrade to an empty list on failure.
export async function loadCustomersSafe(q = '') {
  try {
    // Dedicated lightweight lookup (single indexed query) — scales to a
    // 5000+ customer typeahead without the grid list's COUNT + aggregates.
    const resp = await client.get('/api/erp/masters/customers/lookups/customers', { params: { q, limit: 50 } });
    const data = unwrap(resp);
    const rows = Array.isArray(data) ? data : (data && Array.isArray(data.rows) ? data.rows : []);
    return { rows, error: null };
  } catch (e) {
    return { rows: [], error: e };
  }
}

export async function loadCustomerByIdSafe(id) {
  try {
    const resp = await client.get(`/api/erp/masters/customers/${encodeURIComponent(id)}`);
    return { customer: unwrap(resp), error: null };
  } catch (e) {
    return { customer: null, error: e };
  }
}

// Active HRMS salespersons for the manual-override picker. Source of truth
// is the HRMS employee master (is_salesperson + active status). Degrades to
// an empty list on failure.
export async function loadSalespersonsSafe(q = '') {
  try {
    const resp = await client.get('/api/erp/hrms/employees/salespersons', { params: { q, limit: 500 } });
    const data = unwrap(resp);
    const rows = data && Array.isArray(data.rows) ? data.rows : (Array.isArray(data) ? data : []);
    return { rows, error: null };
  } catch (e) {
    return { rows: [], error: e };
  }
}

// `ctx` (Stage 6PL-1c) is the document's pricing context — { customerId,
// asOfDate } straight off the dropdowns payload, so the picker and the rates
// the editor is already holding can never disagree about which customer they
// belong to. With no ctx this is the plain item search it has always been.
export async function loadItemsSafe(q = '', ctx = null) {
  try {
    const params = { search: q, limit: 50 };
    if (ctx && ctx.customerId) params.customerId = ctx.customerId;
    if (ctx && ctx.asOfDate) params.date = ctx.asOfDate;
    const data = await proformaApi.itemLookup(params);
    const rows = data && Array.isArray(data.rows) ? data.rows : (Array.isArray(data) ? data : []);
    return { rows, error: null };
  } catch (e) {
    return { rows: [], error: e };
  }
}

// Single safe loader for all proforma dropdowns. Never throws.
export async function loadProformaDropdownsSafe(ctx = null) {
  const empty = {
    materialTypes: [], glassTypes: [], thicknesses: [], categories: [], gstRates: [],
    fabrications: [], processes: [], manualCharges: [], states: [], wastageModes: [], inputUnits: [], priceUoms: [], areaModes: [],
    jumboCondition: null,
    priceList: null,
    errors: { all: true }
  };
  try {
    const params = {};
    if (ctx && ctx.customerId) params.customerId = ctx.customerId;
    if (ctx && ctx.date) params.date = ctx.date;
    // Migration 0185 — the document's own price-list choice, so the overlay
    // and the badge describe THIS document rather than the customer's default.
    if (ctx && ctx.priceListMode) params.priceListMode = ctx.priceListMode;
    if (ctx && ctx.priceListOverrideId) params.priceListOverrideId = ctx.priceListOverrideId;
    const data = await proformaApi.getDropdowns(params);
    if (!data) return empty;
    return {
      materialTypes: data.materialTypes || [],
      glassTypes: data.glassTypes || [],
      thicknesses: data.thicknesses || [],
      categories: data.categories || [],
      gstRates: data.gstRates || [],
      fabrications: data.fabrications || [],
      processes: data.processes || [],
      // Stage 6PL-1d — the Manual Charge catalogue, already carrying this
      // customer's rates. The bottom panel's Description picker reads it.
      manualCharges: data.manualCharges || [],
      states: data.states || [],
      wastageModes: data.wastageModes || [],
      inputUnits: data.inputUnits || [],
      priceUoms: data.priceUoms || [],
      areaModes: data.areaModes || [],
      // Jumbo-size condition (display-only): drives the grid "( JUMBO SIZE )" tag.
      jumboCondition: data.jumboCondition || null,
      // Stage 6PL-1c — which price lists reach this customer and how many rates
      // they moved. The item picker reads customerId/asOfDate straight off this,
      // so it always asks about the same customer these rates came from.
      priceList: data.priceList || null,
      errors: data.errors || null
    };
  } catch (_e) {
    return empty;
  }
}

export default proformaApi;
